import { isIos, shouldUseRedirect } from '../../utilities/platform';

/**
 * Both of these moved to utilities/platform once Dropbox and OneDrive needed
 * the identical answer. Three copies of a browser sniff that must agree is two
 * copies too many: if one drifts, a provider quietly goes back to opening
 * popups that iOS throws away. Re-exported here so gisClient and gapiService
 * keep importing them from the module that owns the rest of this flow.
 */
export { isIos, shouldUseRedirect };

/**
 * The top-level redirect half of Google sign-in.
 *
 * GIS's token client is popup-only - "Due to security concerns, only the
 * dialog UX is supported" - and a popup cannot deliver a token to an iOS
 * home-screen PWA. `window.open` there hands the page to Safari proper: a
 * different browsing context, with no `window.opener` pointing back into the
 * standalone one. The token arrives in a window that cannot reach us.
 *
 * Note what that means for diagnosis. The gesture is *not* the problem here -
 * gapiService.authorize calls requestToken synchronously from the click, which
 * is correct. Calling it even earlier would change nothing. The delivery
 * channel is what is broken, so the fix has to replace the channel: navigate
 * the whole page to Google and let it redirect back with the token in the
 * fragment. Same shape of fix MOBILE-IOS.md section 1c prescribes for Dropbox.
 *
 * Why the implicit flow (`response_type=token`) rather than code+PKCE, which
 * is what Google now recommends: Google's token endpoint rejects a PKCE
 * exchange that carries no client secret - a documented wart, not a
 * misreading. There is nowhere to put a secret here, because this repository
 * is public, and standing up a backend to hold one is a bigger change than
 * this fix. Implicit needs no secret and matches what gisClient already
 * assumes: an access token, no refresh, re-authorised by a button.
 */

const authEndpoint = 'https://accounts.google.com/o/oauth2/v2/auth';

/**
 * Must match an entry in the OAuth client's Authorised redirect URIs byte for
 * byte - scheme, host, path, no trailing slash. Production is registered as
 * https://fadila.starcraft.is/google-redirect. A dev origin needs registering
 * separately or sign-in fails there with redirect_uri_mismatch.
 */
export const redirectPath = '/google-redirect';

export function redirectUri() {
  return window.location.origin + redirectPath;
}

/** Where we stash what we need to make sense of the trip back. */
const stateKey = 'gisRedirectState';

/** A round trip that has taken longer than this is not one we still expect. */
const stateLifetimeMs = 10 * 60 * 1000;

export interface RedirectIntent {
  /** Make Google Drive the storage provider once a token lands. */
  configureProvider?: boolean;
}

interface StoredState {
  state: string;
  returnTo: string;
  intent: RedirectIntent;
  createdAt: number;
}

export interface RedirectResult {
  accessToken: string;
  expiresIn: number;
  scope: string;
  returnTo: string;
  intent: RedirectIntent;
}

export class RedirectError extends Error {
  constructor(message: string, public readonly returnTo: string) {
    super(message);
    this.name = 'RedirectError';
  }
}

/**
 * localStorage rather than sessionStorage on purpose. iOS may service the
 * cross-origin hop by suspending or even killing the standalone app, and a
 * relaunch starts a fresh session - which would strand the flow at exactly the
 * point it is meant to fix. The value is a CSRF nonce and a path, so it keeps
 * badly without consequence.
 */
function readState(): StoredState | undefined {
  try {
    const raw = localStorage.getItem(stateKey);

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as StoredState;

    if (!parsed || !parsed.state || Date.now() - parsed.createdAt > stateLifetimeMs) {
      return undefined;
    }

    return parsed;
  }
  catch {
    return undefined;
  }
}

function clearState() {
  try {
    localStorage.removeItem(stateKey);
  }
  catch { }
}

/** Not security-critical beyond CSRF, but crypto where it is available. */
function randomState() {
  const bytes = new Uint8Array(16);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  }
  else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.prototype.map
    .call(bytes, (b: number) => ('0' + b.toString(16)).slice(-2))
    .join('');
}


/**
 * Whether a silent token request is worth making at all.
 *
 * Not anywhere that needs the redirect - and there it is worse than useless.
 * `prompt: ''` is silent only where Google's cookies are still sent; where
 * they are not, GIS gives up on the quiet path and opens a *window* instead.
 * On a home-screen launch that means iOS asking permission for a popup before
 * the app has drawn anything, and the page losing focus while gapi.client is
 * still fetching its discovery documents - which is exactly the sequence that
 * left startup wedged on the loading checklist.
 */
export function canRenewSilently() {
  return !shouldUseRedirect();
}

/**
 * Navigates away. Nothing after this call runs, so an authorize() that took
 * this path returns a promise that never settles - by design.
 */
export function beginRedirect(clientId: string, scope: string, intent?: RedirectIntent) {
  const state = randomState();

  try {
    localStorage.setItem(stateKey, JSON.stringify({
      state,
      // Come back to wherever the user actually was, not a fixed landing page.
      returnTo: window.location.pathname + window.location.search,
      intent: intent || {},
      createdAt: Date.now(),
    } as StoredState));
  }
  catch {
    // Private browsing. The trip out still works; the trip back will fail the
    // state check and say so, which beats navigating away silently.
  }

  const params = [
    ['client_id', clientId],
    ['redirect_uri', redirectUri()],
    ['response_type', 'token'],
    ['scope', scope],
    ['state', state],
    // Scopes are cumulative here for the same reason they are in
    // gapiService.scopesToRequest - asking for one must not drop the rest.
    ['include_granted_scopes', 'true'],
  ]
    .map(pair => `${encodeURIComponent(pair[0])}=${encodeURIComponent(pair[1])}`)
    .join('&');

  // assign, not replace: leaves the app in history so back does something sane.
  window.location.assign(`${authEndpoint}?${params}`);
}

function parseParams(str: string) {
  const out: { [key: string]: string } = {};

  str.replace(/^[#?]/, '').split('&').forEach(function (pair) {
    if (!pair) {
      return;
    }

    const idx = pair.indexOf('=');
    const key = decodeURIComponent(idx === -1 ? pair : pair.slice(0, idx));

    out[key] = idx === -1
      ? ''
      : decodeURIComponent(pair.slice(idx + 1).replace(/\+/g, ' '));
  });

  return out;
}

/**
 * Reads the token Google handed back. Only meaningful on the redirect route.
 *
 * Throws RedirectError carrying a usable returnTo, so a failure can still put
 * the user back in the app rather than stranding them on a blank callback page.
 */
export function completeRedirect(fragment: string, search: string): RedirectResult {
  const stored = readState();
  const returnTo = (stored && stored.returnTo) || '/app';

  clearState();

  // Implicit returns everything in the fragment, but a request rejected before
  // Google got as far as the flow comes back on the query string.
  const params = parseParams(fragment);
  const queryParams = parseParams(search);
  const error = params.error || queryParams.error;

  if (error) {
    throw new RedirectError(
      params.error_description || queryParams.error_description || error,
      returnTo,
    );
  }

  if (!stored) {
    throw new RedirectError(
      'Google sign-in could not be completed - the request it answers has expired.',
      returnTo,
    );
  }

  if (!params.state || params.state !== stored.state) {
    throw new RedirectError('Google sign-in returned an unexpected response.', returnTo);
  }

  if (!params.access_token) {
    throw new RedirectError('Google sign-in returned no access token.', returnTo);
  }

  return {
    accessToken: params.access_token,
    expiresIn: Number(params.expires_in) || 3600,
    scope: params.scope || '',
    returnTo,
    intent: stored.intent || {},
  };
}
